import { useParams } from "react-router-dom";
import { AddMedicineForm } from "@/components/settings/AddMedicineForm";
import { AddAppointmentForm } from "@/components/settings/AddAppointmentForm";
import { AddBlockedTimeForm } from "@/components/settings/AddBlockedTimeForm";
import { AppointmentsCalendar } from "@/components/settings/AppointmentsCalendar";
import { AppointmentListPage } from "@/components/appointments/AppointmentListPage";
import { AwaitingPaymentsPage } from "@/components/appointments/AwaitingPaymentsPage";
import { AddFoodEntryForm } from "@/components/settings/AddFoodEntryForm";
import { ViewFoodItems } from "@/components/settings/ViewFoodItems";
import { ViewFood } from "@/components/settings/ViewFood";
import { ViewRecipes } from "@/components/settings/ViewRecipes";
import { AddRecipeForm } from "@/components/settings/AddRecipeForm";
import { ViewServingUnits } from "@/components/settings/ViewServingUnits";
import { UpdateFoodEntryForm } from "@/components/settings/UpdateFoodEntryForm";
import { FoodServings } from "@/components/settings/FoodServings";
import { FoodSources } from "@/components/settings/FoodSources";
import { FoodBrands } from "@/components/settings/FoodBrands";
import { DiseaseLibraryHome } from "@/components/settings/diseases";
import { StaffList } from "@/components/settings/StaffList";
import { CreateStaffForm } from "@/components/settings/CreateStaffForm";
import { EditStaffForm } from "@/components/settings/EditStaffForm";
import { CommunicationLogs } from "@/components/settings/CommunicationLogs";
import { ManageServicesHome } from "@/components/manage-services";
import { ManagePromoCodesHome } from "@/components/manage-promo-codes";
import { Transactions } from "@/components/transactions";
import { MaintenanceWindowsHome } from "@/components/maintenance-windows";
import { ConsultationRecordings } from "@/components/settings/ConsultationRecordings";
import { useAuthStore } from "@/stores/authStore";
import { AccessDenied } from "@/components/AccessDenied";
import { getConsoleSettingsPermissionRequirement } from "@/constants/consoleNavigation";
import { meetsPermissionRequirement } from "@/constants/permissions";
import { cn } from "@/lib/utils";

const usesOverflowHidden = (section?: string, subsection?: string) =>
  (section === "appointments" &&
    (subsection === "calendar" ||
      subsection === "list" ||
      subsection === "awaiting-payment")) ||
  (section === "nutrition" &&
    [
      "view-food-items",
      "view-food",
      "view-recipes",
      "add-recipe",
      "serving-units",
      "add-food-entry",
      "update-food-entry",
      "food-servings",
      "food-sources",
      "food-brands",
    ].includes(subsection ?? "")) ||
  section === "diseases" ||
  (section === "staff" && subsection === "view-staff") ||
  (section === "communications" && subsection === "logs") ||
  (section === "transactions" && subsection === "logs") ||
  section === "maintenance-windows" ||
  (section === "services" && subsection === "manage-services") ||
  (section === "promo-codes" && subsection === "manage-promo-codes");

const usesFullHeight = (section?: string, subsection?: string) =>
  usesOverflowHidden(section, subsection) ||
  (section === "consultation-recordings" && subsection === "overview");

const Settings = () => {
  const { hasAnyPermission, hasPermission } = useAuthStore();
  const { section, subsection } = useParams<{
    section: string;
    subsection: string;
  }>();

  const selectedSection = section || "appointments";
  const selectedSubSection = subsection || "calendar";
  const isMaintenanceWindows = selectedSection === "maintenance-windows";
  const permissionRequirement = getConsoleSettingsPermissionRequirement(
    selectedSection,
    selectedSubSection
  );
  const canAccessSelectedSection =
    permissionRequirement !== null &&
    meetsPermissionRequirement(permissionRequirement, {
      hasPermission,
      hasAnyPermission,
    });

  if (!canAccessSelectedSection) {
    return (
      <AccessDenied description="You do not have permission to access this settings section." />
    );
  }

  const renderContent = () => {
    if (
      selectedSection === "appointments" &&
      selectedSubSection === "calendar"
    ) {
      return <AppointmentsCalendar />;
    }

    if (selectedSection === "appointments" && selectedSubSection === "list") {
      return <AppointmentListPage />;
    }

    if (
      selectedSection === "appointments" &&
      selectedSubSection === "awaiting-payment"
    ) {
      return <AwaitingPaymentsPage />;
    }

    if (
      selectedSection === "appointments" &&
      selectedSubSection === "add-appointment"
    ) {
      return <AddAppointmentForm />;
    }

    if (
      selectedSection === "appointments" &&
      selectedSubSection === "blocked-time"
    ) {
      return <AddBlockedTimeForm />;
    }

    if (
      selectedSection === "medicine" &&
      selectedSubSection === "add-medicine"
    ) {
      return <AddMedicineForm />;
    }

    if (
      selectedSection === "nutrition" &&
      selectedSubSection === "view-food-items"
    ) {
      return <ViewFoodItems />;
    }

    if (selectedSection === "nutrition" && selectedSubSection === "view-food") {
      return <ViewFood />;
    }

    if (
      selectedSection === "nutrition" &&
      selectedSubSection === "view-recipes"
    ) {
      return <ViewRecipes />;
    }

    if (
      selectedSection === "nutrition" &&
      selectedSubSection === "add-recipe"
    ) {
      return <AddRecipeForm />;
    }

    if (
      selectedSection === "nutrition" &&
      selectedSubSection === "serving-units"
    ) {
      return <ViewServingUnits />;
    }

    if (
      selectedSection === "nutrition" &&
      selectedSubSection === "add-food-entry"
    ) {
      return <AddFoodEntryForm />;
    }

    if (
      selectedSection === "nutrition" &&
      selectedSubSection === "update-food-entry"
    ) {
      return <UpdateFoodEntryForm />;
    }

    if (
      selectedSection === "nutrition" &&
      selectedSubSection === "food-servings"
    ) {
      return <FoodServings />;
    }

    if (
      selectedSection === "nutrition" &&
      selectedSubSection === "food-sources"
    ) {
      return <FoodSources />;
    }

    if (
      selectedSection === "nutrition" &&
      selectedSubSection === "food-brands"
    ) {
      return <FoodBrands />;
    }

    if (selectedSection === "diseases") {
      return <DiseaseLibraryHome />;
    }

    if (selectedSection === "staff" && selectedSubSection === "view-staff") {
      return <StaffList />;
    }

    if (selectedSection === "staff" && selectedSubSection === "create-staff") {
      return <CreateStaffForm />;
    }

    if (selectedSection === "staff" && selectedSubSection === "edit-staff") {
      return <EditStaffForm />;
    }

    if (selectedSection === "communications" && selectedSubSection === "logs") {
      return <CommunicationLogs />;
    }

    if (selectedSection === "transactions" && selectedSubSection === "logs") {
      return <Transactions />;
    }

    if (
      selectedSection === "services" &&
      selectedSubSection === "manage-services"
    ) {
      return <ManageServicesHome />;
    }

    if (isMaintenanceWindows) {
      return <MaintenanceWindowsHome />;
    }

    if (
      selectedSection === "promo-codes" &&
      selectedSubSection === "manage-promo-codes"
    ) {
      return <ManagePromoCodesHome />;
    }

    if (
      selectedSection === "consultation-recordings" &&
      selectedSubSection === "overview"
    ) {
      return <ConsultationRecordings />;
    }

    return null;
  };

  return (
    <div className="flex h-full overflow-hidden p-2 sm:p-3">
      <div className="flex min-h-0 min-w-0 flex-1 gap-2 sm:gap-3">
        <div
          className={cn(
            "flex-1 min-h-0 min-w-0",
            usesOverflowHidden(selectedSection, selectedSubSection)
              ? "flex flex-col overflow-hidden"
              : "overflow-y-auto"
          )}
        >
          <div
            className={cn(
              "w-full",
              usesFullHeight(selectedSection, selectedSubSection)
                ? "flex-1 min-h-0 flex flex-col"
                : "max-w-4xl mx-auto px-2 sm:px-4"
            )}
          >
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
